import React, { useEffect, useState } from 'react';

import Typography  from '@mui/material/Typography';
import Box  from '@mui/material/Box';
import Link  from '@mui/material/Link';
import Grid  from '@mui/material/Grid';
import InputLabel  from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { Theme } from '@mui/material/styles/createTheme';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { pathOr, map, head, equals, length, isEmpty } from 'ramda';
import parseGooglePlace from 'parse-google-place';
import PlacesAutocomplete, {
  geocodeByAddress,
} from 'react-places-autocomplete';
import AddressModal from './AddressModal';
import { isNotEmpty } from 'src/utilities/hasValue';
import { doNothing } from 'src/services/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    item: {},
    label: {
      color: theme.palette.basic.grey,
      fontSize: '12px',
      fontWeight: 400,
    },
    container: {
      display: 'flex',
      alignItems: 'center',
    },
    value: {
      flexGrow: 1,
      fontSize: '14px',
      fontWeight: 600,
    },
    wrapper: {
      border: `1px solid ${theme.palette.basic.lightGrey}`,
      borderRadius: 3,
      backgroundColor: theme.palette.mainLight,
      paddingTop: 8,
      paddingBottom: 8,
      marginTop: 5,
    },
    locationItem: {
      padding: '8px 14px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(240, 235, 250, 0.4)',
      },
    },
    inputBox: {
      marginTop: 8,
      marginBottom: 8,
    },
  })
);

interface Props {
  resourceData?: any;
  doShowWhenError?: any | '';
  onhandleSelect?: any; // (addressString: string, name: string, placeId: string) => void;
  name?: any;
  className?: any;
  value?: any;
  inputSize?: any;
  placeholder?: string | undefined;
  disabled?: boolean;
  onChange?: any;
  sx?: any;
  showUnit?: boolean;
  handleSuitChange?: any;
  handleOnBlur?: any;
  isSuite?: boolean;
  testId?: string;
  autoFocus?: boolean;
}
const AddressDropdown: React.FC<Props> = (props: Props) => {
  const {
    onhandleSelect,
    name,
    className,
    doShowWhenError,
    inputSize,
    placeholder,
    disabled,
    onChange,
    value,
    sx,
    showUnit,
    handleSuitChange,
    handleOnBlur,
    isSuite,
    testId,
    autoFocus
  } = props;
  const classes = useStyles();
  const [address, setAddress] = useState(value);
  const [formattedAddress, setFormattedAddress] = useState<any>({
    street: '',
    city: '',
    state: '',
    zipcode: '',
    geoId: '',
  });
  const [show, setShow] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [suite, setSuite] = useState('');
  const [location, setLocation] = useState({
    address: '',
    unit: '',
    geoId: '',
  });
  useEffect(() => {
    if (value) {
      setAddress(value);
    } else {
      setAddress('')
    }
  }, [value]);

  const handleSetAddress = (e: any, suit: any) => {
    setAddress(e);
    setSuite(suit);
    // @ts-ignore next-line
    isNotEmpty(onhandleSelect) && onhandleSelect(e, name, '');
    setModalOpen(false);
  };

  useEffect(() => {
    if (isSuite) {
      isNotEmpty(handleSuitChange) && handleSuitChange(suite, 'suite');
    }
  }, [suite]);

  const handleChange = (address: any) => {
    setAddress(address);
    if (onChange) {
      onChange(name, address);
    }
  };

  const handleChangeUnit = (e: any) => {
    setSuite(e.target.value);
  };
  const handleSelect = (address: any) => {
    setShow(false);
    setAddress(address);
    setLocation({ ...location, address });

    geocodeByAddress(address)
      .then(async (results: any) => {
        const placeId = pathOr('', ['place_id'], head(results));
        const formatted_address = pathOr(
          '',
          ['formatted_address'],
          head(results)
        );
        /**
         * parseGooglePlace - This method gettig the street,city,state,zip based geocodeByAddress results
         */
        const p = parseGooglePlace({
          address_components: pathOr('', ['address_components'], head(results)),
        });
        const { address, city, stateShort, zipCode } = p;
        setFormattedAddress({
          street: address,
          city,
          state: stateShort,
          zipcode: zipCode,
          geoId: placeId,
        });
        setModalOpen(true);
        await setLocation({
          ...location,
          address: formatted_address,
          geoId: placeId,
        });
      })
      .catch((error: any) => console.error('Error', error));
  };
  const searchOptions = {
    componentRestrictions: { country: ['us'] },
  };
  const onError = (status: any, clearSuggestions: () => void) => {
    setShow(true);
    clearSuggestions();
  };

  const toggleOpen = () => {
    setModalOpen(true);
  };
  const toggleClose = () => {
    setModalOpen(false);
    setAddress(value);
  };
  return (
    <React.Fragment>
      <PlacesAutocomplete
        searchOptions={searchOptions}
        value={address}
        onChange={handleChange}
        onSelect={handleSelect}
        onError={onError}
      >
        {/*@ts-ignore */}
        {({ getSuggestionItemProps, getInputProps, suggestions }) => {
          return (
            <Grid item container flexDirection="column">
              <Grid item container flexDirection="row" flexWrap="nowrap">
                <Grid item xs={showUnit ? 9 : 12}>
                  <TextField
                    size={inputSize ?? 'normal'}
                    margin={inputSize === 'small' ? "dense" : "none"}
                    className={
                      inputSize ? classes.inputBox : 'location-search-input'
                    }
                    name={name}
                    inputProps={{ 
                      className: className,
                      "data-test-id": testId ? testId : name,
                      autoFocus: autoFocus
                    }}
                    placeholder={placeholder ?? 'Search Places ...'}
                    {...getInputProps({ onBlur: modalOpen ? doNothing : handleOnBlur })}
                    disabled={disabled}
                    value={address}
                    sx={sx}
                  />
                </Grid>
                {showUnit && (
                  <Grid item pl={1} xs={3} sx={{ marginTop: '-23px' }}>
                    <InputLabel sx={{ marginBottom: '6px' }}>Unit #</InputLabel>
                    <TextField
                      placeholder="Unit #"
                      value={suite}
                      onChange={handleChangeUnit}
                    />
                  </Grid>
                )}
              </Grid>
              {isEmpty(suggestions) &&
                show &&
                equals('POPUP', doShowWhenError) &&
                !modalOpen && (
                  <Box className={classes.wrapper}>
                    <Box className={classes.locationItem}>
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={toggleOpen}
                      >
                        I can't find the address
                      </Link>
                    </Box>
                  </Box>
                )}
              {!!length(suggestions) && (
                <Box className={classes.wrapper}>
                  {map(
                    (suggestion: any) => (
                      <Box
                        className={classes.locationItem}
                        {...getSuggestionItemProps(suggestion)}
                      >
                        <Typography>
                          {pathOr('', ['description'], suggestion)}
                        </Typography>
                      </Box>
                    ),
                    suggestions
                  )}
                </Box>
              )}
            </Grid>
          );
        }}
      </PlacesAutocomplete>
      {modalOpen &&
        <AddressModal
          open={modalOpen}
          toggleClose={toggleClose}
          handleSetAddress={handleSetAddress}
          formattedAddress={formattedAddress}
          suite={suite}
        />
      }
    </React.Fragment>
  );
};

export default AddressDropdown;
